<script lang="ts">
	/**
	 * `/Settings/Sellers` — the legal entities invoices are issued by (§4.6).
	 *
	 * Two things this screen has to say plainly, because both are counter-intuitive and both are
	 * expensive to learn the hard way.
	 *
	 * **Editing a seller does not change invoices already issued.** Every issued document carries
	 * its own snapshot of the seller taken at issue, which is what makes an invoice a record rather
	 * than a view of today's configuration. An admin correcting an address will reasonably assume
	 * the correction is retroactive; it is not, and it must not be.
	 *
	 * **The fields are not optional in the way a form makes them look.** Article 226 of the VAT
	 * Directive requires the seller's full name, full address and VAT identification number on the
	 * document, and a German invoice additionally carries the register entry. A store that leaves
	 * them blank issues documents its customers' accountants reject — which surfaces weeks later as
	 * a support request rather than as an error here.
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Input, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell, Textarea, Toggle } from 'flowbite-svelte';
	import { CountrySelect, EmptyState, FormField, FormModal, ImageField, PageHeader, Panel, SelectField } from '../../components';
	import { createSeller, listSellers, promoteSellerDefault, setSellerNextNumber, updateSeller } from '../../AppNetwork';
	import type { Seller } from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';

	let sellers: Seller[] = [];
	let loading: boolean = true;
	let loadError: string = '';
	let saving: boolean = false;

	/** The row being edited, or null when adding one. */
	let editing: Seller | null = null;
	let isOpen: boolean = false;

	function emptyDraft() {
		return {
			name: '',
			legalName: '',
			addressLine1: '',
			addressLine2: '',
			addressPostalCode: '',
			addressCity: '',
			addressCountry: '',
			vatID: '',
			taxNumber: '',
			registrationNumber: '',
			registrationCourt: '',
			email: '',
			phone: '',
			website: '',
			footerNote: '',
			invoiceMessage: '',
			invoiceNumberPrefix: '',
			logoUUID: null as string | null,
			logoURL: null as string | null,
			isActive: true
		};
	}

	let draft = emptyDraft();

	$: canSave = draft.name.trim().length > 0 && draft.legalName.trim().length > 0;

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			sellers = (await listSellers()).items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	function fnStartAdd(): void {
		editing = null;
		draft = emptyDraft();
		isOpen = true;
	}

	function fnStartEdit(seller: Seller): void {
		editing = seller;
		draft = {
			name: seller.name,
			legalName: seller.legalName,
			addressLine1: seller.addressLine1 ?? '',
			addressLine2: seller.addressLine2 ?? '',
			addressPostalCode: seller.addressPostalCode ?? '',
			addressCity: seller.addressCity ?? '',
			addressCountry: seller.addressCountry ?? '',
			vatID: seller.vatID ?? '',
			taxNumber: seller.taxNumber ?? '',
			registrationNumber: seller.registrationNumber ?? '',
			registrationCourt: seller.registrationCourt ?? '',
			email: seller.email ?? '',
			phone: seller.phone ?? '',
			website: seller.website ?? '',
			footerNote: seller.footerNote ?? '',
			invoiceMessage: seller.invoiceMessage ?? '',
			invoiceNumberPrefix: seller.invoiceNumberPrefix,
			logoUUID: seller.logoUUID ?? null,
			logoURL: seller.logoURL ?? null,
			isActive: seller.isActive
		};
		isOpen = true;
	}

	/** Empty means "not set" rather than the empty string, so a cleared field clears the column. */
	function nullable(value: string): string | null {
		return value.trim() === '' ? null : value.trim();
	}

	async function fnSave(): Promise<void> {
		saving = true;
		try {
			const input = {
				name: draft.name.trim(),
				legalName: draft.legalName.trim(),
				addressLine1: nullable(draft.addressLine1),
				addressLine2: nullable(draft.addressLine2),
				addressPostalCode: nullable(draft.addressPostalCode),
				addressCity: nullable(draft.addressCity),
				addressCountry: nullable(draft.addressCountry),
				vatID: nullable(draft.vatID),
				taxNumber: nullable(draft.taxNumber),
				registrationNumber: nullable(draft.registrationNumber),
				registrationCourt: nullable(draft.registrationCourt),
				email: nullable(draft.email),
				phone: nullable(draft.phone),
				website: nullable(draft.website),
				footerNote: nullable(draft.footerNote),
				invoiceMessage: nullable(draft.invoiceMessage),
				invoiceNumberPrefix: draft.invoiceNumberPrefix.trim().toUpperCase(),
				logoUUID: draft.logoUUID,
				isActive: draft.isActive
			};

			if (editing === null) {
				await createSeller(input);
				toastSuccess(`${input.legalName} added.`);
			} else {
				await updateSeller(editing.uuid, input);
				toastSuccess(`${input.legalName} saved.`);
			}
			isOpen = false;
			editing = null;
			await fnLoad();
		} catch (error) {
			toastError('The seller was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/**
	 * Continuing a series another system started (§4.6). This year only, forward only; the server
	 * refuses a number already issued, and the form says so before it is asked.
	 */
	let numbering: Seller | null = null;
	let numberingSeries: 'invoice' | 'credit_note' = 'invoice';
	let numberingNext: string = '';
	let numberingSaving: boolean = false;

	$: numberingPosition =
		numbering === null ? null : numberingSeries === 'invoice' ? numbering.numbering.invoice : numbering.numbering.creditNote;
	$: numberingParsed = /^\d{1,6}$/.test(numberingNext.trim()) ? Number(numberingNext.trim()) : null;
	$: numberingBackwards =
		numberingParsed !== null && numberingPosition !== null && numberingParsed <= numberingPosition.lastIssued;
	$: canSaveNumbering = !numberingSaving && numberingParsed !== null && numberingParsed >= 1 && !numberingBackwards;

	/** How a number in this seller's series is printed, so the form previews the real thing. */
	function fnFormatNumber(seller: Seller, series: 'invoice' | 'credit_note', value: number): string {
		return [
			seller.invoiceNumberPrefix === '' ? null : seller.invoiceNumberPrefix,
			series === 'credit_note' ? 'CN' : null,
			String(seller.numbering?.year ?? new Date().getUTCFullYear()),
			String(value).padStart(6, '0')
		]
			.filter(part => part !== null)
			.join('-');
	}

	function fnStartNumbering(seller: Seller): void {
		numbering = seller;
		numberingSeries = 'invoice';
		numberingNext = String(seller.numbering?.invoice.next ?? 1);
	}

	async function fnSaveNumbering(): Promise<void> {
		if (numbering === null || numberingParsed === null) return;
		numberingSaving = true;
		try {
			await setSellerNextNumber(numbering.uuid, numberingSeries, numberingParsed);
			toastSuccess(`The next ${numberingSeries === 'invoice' ? 'invoice' : 'credit note'} will be ${fnFormatNumber(numbering, numberingSeries, numberingParsed)}.`);
			numbering = null;
			await fnLoad();
		} catch (error) {
			toastError('The series was not moved.', (error as Error).message);
		} finally {
			numberingSaving = false;
		}
	}

	async function fnPromote(seller: Seller): Promise<void> {
		try {
			await promoteSellerDefault(seller.uuid);
			toastSuccess(`${seller.legalName} is now the default seller.`);
			await fnLoad();
		} catch (error) {
			toastError('The default was not changed.', (error as Error).message);
		}
	}

	/** What a document would be missing if it were issued by this seller today. */
	function missingOf(seller: Seller): string[] {
		const required: [string, string | null][] = [
			['an address', seller.addressLine1],
			['a city', seller.addressCity],
			['a country', seller.addressCountry],
			['a VAT ID', seller.vatID]
		];
		return required.filter(([, value]) => value === null || value === '').map(([label]) => label);
	}

	onMount(() => void fnLoad());
</script>

<PageHeader title="Sellers">
	<svelte:fragment slot="description">
		The legal entities invoices are issued by.
	</svelte:fragment>
</PageHeader>

<Panel title="Sellers" faIcon="building">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={fnStartAdd}>
			<i class="fa-solid fa-plus mr-2" />Add a seller
		</Button>
	</svelte:fragment>

	<!--
		Said next to the table rather than buried in help text. An admin editing a company address
		will assume the correction applies to past invoices; it does not, and the reason it must not
		is the whole basis for these being ordinary editable rows.
	-->
	<p class="text-sm text-default mb-4">
		<i class="fa-solid fa-circle-info mr-2" />A product with no seller is sold by the default.
		Editing a seller changes <strong>future</strong> invoices only: every invoice already issued
		carries the details that were correct on the day it was issued, which is what makes it a
		record rather than a view.
	</p>

	{#if loading}
		<EmptyState variant="loading" title="Loading sellers…" />
	{:else if loadError.length > 0}
		<EmptyState variant="error" title="The sellers could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
		</EmptyState>
	{:else if sellers.length === 0}
		<EmptyState
			title="No seller is configured."
			description="Until one exists the Store cannot issue its own invoices, and a checkout that reaches the point of issuing one will refuse rather than produce a document with nobody's name on it."
		>
			<Button color="primary" size="sm" on:click={fnStartAdd}>Add the first seller</Button>
		</EmptyState>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Seller</TableHeadCell>
				<TableHeadCell>VAT ID</TableHeadCell>
				<TableHeadCell>Series</TableHeadCell>
				<TableHeadCell>Products</TableHeadCell>
				<TableHeadCell>State</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each sellers as seller (seller.uuid)}
					<TableBodyRow>
						<TableBodyCell class="dataTable">
							<div class="font-medium">{seller.legalName}</div>
							<div class="text-default text-xs">{seller.name}</div>
						</TableBodyCell>
						<TableBodyCell class="dataTable">{seller.vatID ?? '—'}</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if seller.numbering}
								<div>Next: {fnFormatNumber(seller, 'invoice', seller.numbering.invoice.next)}</div>
								<div class="text-default text-xs">
									{seller.numbering.invoice.lastIssued === 0
										? `No invoice issued in ${seller.numbering.year} yet`
										: `${seller.numbering.invoice.lastIssued} issued in ${seller.numbering.year}`}
								</div>
							{:else}
								{seller.invoiceNumberPrefix === '' ? 'YYYY-NNNNNN' : `${seller.invoiceNumberPrefix}-YYYY-NNNNNN`}
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{seller.productCount === 0 ? (seller.isDefault ? 'all unassigned' : 'none') : seller.productCount}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex flex-wrap items-center gap-2">
								{#if seller.isDefault}
									<Badge color="blue">Default</Badge>
								{/if}
								{#if seller.isActive}
									<Badge color="green">Active</Badge>
								{:else}
									<Badge color="dark">Inactive</Badge>
								{/if}
								{#if missingOf(seller).length > 0}
									<!--
										Named field by field. "Incomplete" tells an admin there is a
										problem without telling them which one, and the consequence is
										discovered by a customer's accountant rather than here.
									-->
									<Badge color="yellow" title="Required on the invoice">
										Missing {missingOf(seller).join(', ')}
									</Badge>
								{/if}
							</div>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex flex-wrap gap-2">
								<Button size="xs" color="alternative" on:click={() => fnStartEdit(seller)}>
									<i class="fa-solid fa-pen mr-1" />Edit
								</Button>
								{#if seller.numbering}
									<Button size="xs" color="alternative" on:click={() => fnStartNumbering(seller)}>
										<i class="fa-solid fa-hashtag mr-1" />Set next number
									</Button>
								{/if}
								{#if !seller.isDefault && seller.isActive}
									<Button size="xs" color="alternative" on:click={() => void fnPromote(seller)}>
										Make default
									</Button>
								{/if}
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	<FormModal
		bind:open={isOpen}
		title={editing === null ? 'Add a seller' : `Edit ${editing.legalName}`}
		faIcon={editing === null ? 'plus' : 'pen'}
		submitLabel="Save seller"
		busy={saving}
		canSubmit={canSave}
		description="These details appear on every invoice this seller issues from now on. Invoices already issued keep the details they were issued with."
		on:submit={fnSave}
	>
		<div class="grid gap-4 sm:grid-cols-2">
			<FormField id="seller-name" label="Internal name" required description="What this screen calls it.">
				<Input id="seller-name" bind:value={draft.name} placeholder="Abstract" disabled={saving} />
			</FormField>

			<FormField
				id="seller-legal-name"
				label="Legal name"
				required
				description="The registered name, which is what appears on the document."
			>
				<Input id="seller-legal-name" bind:value={draft.legalName} placeholder="Abstract GmbH" disabled={saving} />
			</FormField>

			<FormField id="seller-address1" label="Address" description="Street and number.">
				<Input id="seller-address1" bind:value={draft.addressLine1} disabled={saving} />
			</FormField>

			<FormField id="seller-address2" label="Address line 2">
				<Input id="seller-address2" bind:value={draft.addressLine2} disabled={saving} />
			</FormField>

			<FormField id="seller-postal" label="Postal code">
				<Input id="seller-postal" bind:value={draft.addressPostalCode} disabled={saving} />
			</FormField>

			<FormField id="seller-city" label="City">
				<Input id="seller-city" bind:value={draft.addressCity} disabled={saving} />
			</FormField>

			<FormField id="seller-country" label="Country" description="Where this entity is registered.">
				<CountrySelect
					id="seller-country"
					bind:value={draft.addressCountry}
					disabled={saving}
					allowEmpty
					emptyLabel="Not set"
				/>
			</FormField>

			<FormField
				id="seller-vat"
				label="VAT ID"
				description="Required on an EU invoice, like “DE123456789”."
			>
				<Input id="seller-vat" bind:value={draft.vatID} disabled={saving} />
			</FormField>

			<FormField id="seller-tax-number" label="Tax number" description="Domestic, and a different number from the VAT ID.">
				<Input id="seller-tax-number" bind:value={draft.taxNumber} disabled={saving} />
			</FormField>

			<FormField id="seller-register" label="Register entry" description="“HRB 12345”.">
				<Input id="seller-register" bind:value={draft.registrationNumber} disabled={saving} />
			</FormField>

			<FormField id="seller-court" label="Register court" description="“Amtsgericht München”.">
				<Input id="seller-court" bind:value={draft.registrationCourt} disabled={saving} />
			</FormField>

			<FormField id="seller-email" label="Billing email" description="Where a question about an invoice goes.">
				<Input id="seller-email" bind:value={draft.email} disabled={saving} />
			</FormField>

			<FormField id="seller-phone" label="Phone">
				<Input id="seller-phone" bind:value={draft.phone} disabled={saving} />
			</FormField>

			<FormField id="seller-website" label="Website">
				<Input id="seller-website" bind:value={draft.website} disabled={saving} />
			</FormField>

			<FormField
				id="seller-prefix"
				label="Invoice number prefix"
				description="Prefixes every number this entity issues, so two entities never share a series. Leave empty for “2026-000001”."
			>
				<Input id="seller-prefix" bind:value={draft.invoiceNumberPrefix} maxlength="8" disabled={saving} />
			</FormField>
		</div>

		<!--
			Two free-text fields, and they are deliberately not one. The message is addressed to the
			customer and prints under the totals where it will be read; the footer is the legal band
			at the bottom of the page. A thank-you in the footer is buried beside an IBAN, and bank
			details up here are stated as a pleasantry.
		-->
		<FormField
			id="seller-logo"
			label="Logo"
			description="Printed at the top of every invoice and credit note this entity issues from now on. PNG or JPEG; documents already issued keep the logo they were issued with."
		>
			<ImageField bind:uuid={draft.logoUUID} bind:url={draft.logoURL} disabled={saving} label="Invoice logo" />
		</FormField>

		<FormField
			id="seller-message"
			label="Invoice message"
			description="A closing line on every invoice this entity issues — “Thank you for your business!”. Not printed on a credit note, which is the document that reverses a sale."
		>
			<Textarea id="seller-message" rows="2" bind:value={draft.invoiceMessage} disabled={saving} />
		</FormField>

		<FormField
			id="seller-footer"
			label="Invoice footer"
			description="Bank details, payment terms, a managing director: whatever your jurisdiction wants at the foot of an invoice."
		>
			<Textarea id="seller-footer" rows="3" bind:value={draft.footerNote} disabled={saving} />
		</FormField>

		<div class="flex items-center gap-3">
			<Toggle id="seller-active" bind:checked={draft.isActive} disabled={saving} />
			<label for="seller-active" class="text-sm">In use.</label>
		</div>
	</FormModal>

	{#if numbering !== null}
		<FormModal
			open={true}
			title={`Next number for ${numbering.legalName}`}
			faIcon="hashtag"
			submitLabel="Move the series"
			busy={numberingSaving}
			canSubmit={canSaveNumbering}
			on:submit={fnSaveNumbering}
			on:cancel={() => (numbering = null)}
		>
			<p class="text-sm text-default">
				For carrying on a series another system started. This sets {numbering.numbering.year}'s counter only, and only
				forwards: a number already issued cannot be handed out again.
			</p>
			<FormField id="numbering-series" label="Series">
				<SelectField
					id="numbering-series"
					items={[
						{ value: 'invoice', name: 'Invoices', description: 'The numbering series for invoices.' },
						{ value: 'credit_note', name: 'Credit notes', description: 'The separate numbering series for credit notes, the documents behind refunds.' }
					]}
					bind:value={numberingSeries}
					disabled={numberingSaving}
				/>
			</FormField>
			<FormField
				id="numbering-next"
				label="Next number"
				error={numberingBackwards && numberingPosition !== null
					? `${numberingPosition.lastIssued} has already been issued; the next number must be ${numberingPosition.lastIssued + 1} or higher.`
					: ''}
				description={numberingParsed !== null && !numberingBackwards
					? `The next document will be ${fnFormatNumber(numbering, numberingSeries, numberingParsed)}.`
					: ''}
			>
				<Input id="numbering-next" inputmode="numeric" bind:value={numberingNext} disabled={numberingSaving} />
			</FormField>
		</FormModal>
	{/if}
</Panel>
